/**
 * @fileoverview Landing page content component with detailed Spinner Games description
 */

import React from 'react';
import { useTheme } from '../lib/ThemeContext';
import { 
  Gamepad2, 
  Users, 
  BookOpen, 
  Heart, 
  Brain, 
  Target, 
  Globe, 
  Lightbulb,
  Youtube,
  ArrowRight,
  Play,
  Dice5,
  List,
  Shuffle,
  Palette,
  Settings,
  Share2,
  CheckCircle,
  XCircle
} from 'lucide-react';

const LandingPageContent: React.FC = () => {
  const { isDarkMode } = useTheme();

  const gameTypes = [
    {
      name: "Trivia",
      description: "Engaging quiz games that test and expand knowledge",
      icon: <BookOpen className="w-6 h-6" />,
      blogUrl: "https://spinnergames.fun/blogs/trivia/"
    },
    {
      name: "Truth or Dare",
      description: "Classic party game that encourages openness and fun challenges",
      icon: <Heart className="w-6 h-6" />,
      blogUrl: "https://spinnergames.fun/blogs/truth-or-dare/"
    },
    {
      name: "Which One Are You?",
      description: "Personality-based games that help you discover more about yourself and others",
      icon: <Brain className="w-6 h-6" />,
      blogUrl: "https://spinnergames.fun/blogs/which-one-are-you/"
    },
    {
      name: "Would You Rather",
      description: "Decision-making games that spark interesting conversations",
      icon: <Target className="w-6 h-6" />,
      blogUrl: "https://spinnergames.fun/blogs/would-you-rather/"
    }
  ];

  const utilityTools = [
    {
      name: "Name Picker",
      description: "Random selection tool for fair decision-making in group activities",
      icon: <Users className="w-6 h-6" />
    },
    {
      name: "Dice Roller",
      description: "Digital dice replacement for board games and random selections",
      icon: <Dice5 className="w-6 h-6" />
    },
    {
      name: "Number Generator",
      description: "Random number selection for educational and gaming purposes",
      icon: <List className="w-6 h-6" />
    },
    {
      name: "Color Wheel",
      description: "Interactive color selection tool for creative activities and learning",
      icon: <Palette className="w-6 h-6" />
    },
    {
      name: "Binary Decision Maker",
      description: "Digital quick binary decisions maker for scenarios like yes/no, heads/tails or your custom options",
      icon: <Settings className="w-6 h-6" />
    }
  ];

  const benefits = [
    {
      category: "Educational Value",
      icon: <BookOpen className="w-8 h-8" />,
      color: "blue",
      items: [
        "Knowledge Enhancement: Trivia games expand general knowledge and critical thinking",
        "Learning Through Play: Interactive education that makes learning enjoyable",
        "Skill Development: Improves memory, reasoning, and problem-solving abilities"
      ]
    },
    {
      category: "Social Development",
      icon: <Users className="w-8 h-8" />,
      color: "green",
      items: [
        "Relationship Building: Strengthens bonds between family members and friends",
        "Communication Skills: Encourages open dialogue and active listening",
        "Empathy Development: Understanding different perspectives through interactive games"
      ]
    },
    {
      category: "Digital Detox & Connection",
      icon: <Globe className="w-8 h-8" />,
      color: "purple",
      items: [
        "Screen-Free Interaction: Promotes real-world social engagement",
        "Quality Time: Encourages meaningful face-to-face conversations",
        "Community Building: Fosters a sense of belonging and togetherness"
      ]
    },
    {
      category: "Emotional Intelligence",
      icon: <Heart className="w-8 h-8" />,
      color: "pink",
      items: [
        "Tolerance & Patience: Develops skills to handle diverse personalities and situations",
        "Conflict Resolution: Practice navigating disagreements in a safe environment",
        "Adaptability: Learn to work with different people and adapt to various social contexts"
      ]
    },
    {
      category: "Decision-Making Skills",
      icon: <Target className="w-8 h-8" />,
      color: "orange",
      items: [
        "Critical Thinking: Practice making informed choices",
        "Consensus Building: Learn to work together toward common goals",
        "Leadership Development: Opportunities to take initiative and guide group activities"
      ]
    }
  ];

  const getColorClasses = (color: string) => {
    const colorMap = {
      blue: {
        bg: isDarkMode ? 'bg-blue-900/20' : 'bg-blue-50',
        icon: 'text-blue-600 dark:text-blue-300',
        border: 'border-blue-200 dark:border-blue-700'
      },
      green: {
        bg: isDarkMode ? 'bg-green-900/20' : 'bg-green-50',
        icon: 'text-green-600 dark:text-green-300',
        border: 'border-green-200 dark:border-green-700'
      },
      purple: {
        bg: isDarkMode ? 'bg-purple-900/20' : 'bg-purple-50',
        icon: 'text-purple-600 dark:text-purple-300',
        border: 'border-purple-200 dark:border-purple-700'
      },
      pink: {
        bg: isDarkMode ? 'bg-pink-900/20' : 'bg-pink-50',
        icon: 'text-pink-600 dark:text-pink-300',
        border: 'border-pink-200 dark:border-pink-700'
      },
      orange: {
        bg: isDarkMode ? 'bg-orange-900/20' : 'bg-orange-50',
        icon: 'text-orange-600 dark:text-orange-300',
        border: 'border-orange-200 dark:border-orange-700'
      }
    };
    return colorMap[color as keyof typeof colorMap] || colorMap.blue;
  };

  return (
    <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-0 space-y-16">
      {/* Hero Section */}
      <div className="space-y-6">
        {/* YouTube Watch Link */}
        <div className="flex justify-center mb-4">
          <button
            onClick={() => {
              const videoSection = document.getElementById('video-tutorial');
              if (videoSection) {
                videoSection.scrollIntoView({ behavior: 'smooth' });
              }
            }}
            className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 transition-colors duration-200 group"
          >
            <Youtube className="w-4 h-4 group-hover:scale-110 transition-transform duration-200" />
            <span className="font-medium">Watch</span>
            <span className="text-xs opacity-75">how to play?</span>
          </button>
        </div>

        {/* Header with background strip */}
        <div className="bg-[#1e2434] rounded-lg py-2 px-4">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white flex items-center justify-center whitespace-nowrap">
            <img src="/logo.png" alt="Spinner Games" className="w-6 h-6 sm:w-7 h-7 md:w-8 h-8 mr-2 sm:mr-3 flex-shrink-0 object-contain" />
            <span className="truncate">What is Spinner Games?</span>
          </h1>
        </div>
        
        {/* Description in a box */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 border border-gray-200 dark:border-gray-700">
          <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-300 leading-relaxed text-justify">
            Spinner Games is a digital platform designed to bring people together through interactive entertainment and educational activities. Our mission is to facilitate meaningful connections between family and friends while providing valuable learning experiences that enhance social skills, knowledge, and interpersonal relationships.
          </p>
        </div>
        
        {/* Questionnaire Count Highlight */}
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-lg p-4 border border-green-200 dark:border-green-800">
          <p className="text-lg font-semibold text-green-700 dark:text-green-300 text-center">
            📝 Over 4,000+ engaging questionnaires across all game modes
          </p>
          <p className="text-sm text-green-600 dark:text-green-400 text-center mt-2">
            Endless entertainment with our comprehensive collection of questions and challenges
          </p>
        </div>
        
        {/* Language Support Highlight */}
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-lg p-4 border border-purple-200 dark:border-purple-800">
          <p className="text-lg font-semibold text-purple-700 dark:text-purple-300 text-center">
            🌍 Available in 11 languages to connect people worldwide
          </p>
          <p className="text-sm text-purple-600 dark:text-purple-400 text-center mt-2">
            Bringing more languages sooner as per feedback from users
          </p>
        </div>
      </div>

      {/* Core Features */}
      <div className="space-y-8">
        <div className="bg-green-50 dark:bg-green-900/20 rounded-lg py-2 px-4">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 dark:text-white flex items-center justify-center whitespace-nowrap">
            <img src="/logo.png" alt="Spinner Games" className="w-5 h-5 sm:w-6 h-6 mr-2 sm:mr-3 flex-shrink-0 object-contain" />
            <span className="truncate">Core Features</span>
          </h2>
        </div>
        
        {/* Interactive Games */}
        <div>
          <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center">
            <Gamepad2 className="w-8 h-8 mr-3 text-blue-600 dark:text-blue-300" />
            Interactive Games
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {gameTypes.map((game, index) => (
              <div key={index} className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center text-blue-600 dark:text-blue-300">
                      {game.icon}
                    </div>
                    <h4 className="text-xl font-semibold text-gray-900 dark:text-white">
                      {game.name}
                    </h4>
                  </div>
                  <a
                    href={game.blogUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-3 py-1 text-xs font-medium text-white bg-blue-600 dark:bg-blue-500 rounded-full hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors shrink-0"
                    aria-label={`Read blog about ${game.name}`}
                  >
                    Blog
                  </a>
                </div>
                <p className="text-gray-600 dark:text-gray-300">
                  {game.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Utility Tools */}
        <div>
          <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center">
            <Settings className="w-8 h-8 mr-3 text-green-600 dark:text-green-300" />
            Utility Tools
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {utilityTools.map((tool, index) => (
              <div key={index} className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-12 h-12 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center text-green-600 dark:text-green-300">
                    {tool.icon}
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {tool.name}
                  </h4>
                </div>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  {tool.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* How to Use */}
      <div className="space-y-8">
        <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg py-2 px-4">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 dark:text-white flex items-center justify-center whitespace-nowrap">
            <img src="/logo.png" alt="Spinner Games" className="w-5 h-5 sm:w-6 h-6 mr-2 sm:mr-3 flex-shrink-0 object-contain" />
            <span className="truncate">How to Use Spinner Games</span>
          </h2>
        </div>
        
        {/* Introduction Text */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 border border-gray-200 dark:border-gray-700">
          <p className="text-lg text-gray-600 dark:text-gray-300 text-justify leading-relaxed">
            Spinner Games offers endless possibilities for bringing people together through interactive entertainment. Below are some popular use cases, but the potential applications are limited only by your imagination. Whether you're looking to break the ice at a corporate event, create memorable family moments, or add an element of fun to educational activities, our platform adapts to your unique needs and creative vision.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 border border-gray-200 dark:border-gray-700">
            <div className="w-16 h-16 bg-purple-100 dark:bg-purple-900 rounded-full flex items-center justify-center mx-auto mb-4">
              <Users className="w-8 h-8 text-purple-600 dark:text-purple-300" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 text-center">
              Family & Social Gatherings
            </h3>
            <ul className="space-y-2 text-gray-600 dark:text-gray-300">
              <li>• Organize game nights with friends and family</li>
              <li>• Create memorable bonding experiences</li>
              <li>• Facilitate ice-breaker activities at parties, road trips, etc.</li>
              <li>• Encourage face-to-face interaction</li>
            </ul>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 border border-gray-200 dark:border-gray-700">
            <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-4">
              <BookOpen className="w-8 h-8 text-blue-600 dark:text-blue-300" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 text-center">
              Educational Settings
            </h3>
            <ul className="space-y-2 text-gray-600 dark:text-gray-300">
              <li>• <strong>Schools:</strong> Select students for classroom activities and presentations</li>
              <li>• <strong>Learning:</strong> Use trivia games to reinforce educational content</li>
              <li>• <strong>Color Learning:</strong> Help toddlers and young children learn colors through interactive play</li>
            </ul>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 border border-gray-200 dark:border-gray-700">
            <div className="w-16 h-16 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mx-auto mb-4">
              <Target className="w-8 h-8 text-green-600 dark:text-green-300" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 text-center">
              Professional Environments
            </h3>
            <ul className="space-y-2 text-gray-600 dark:text-gray-300">
              <li>• <strong>Office Meetings:</strong> Fair selection for presentations or task assignments</li>
              <li>• <strong>Team Building:</strong> Organize group activities and decision-making processes</li>
              <li>• <strong>Scrum Meetings:</strong> Randomly select team members for various roles</li>
            </ul>
          </div>
        </div>

        <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-lg p-8 border border-gray-200 dark:border-gray-700">
          <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4 text-center">
            Creative Applications
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Custom Games</h4>
              <p className="text-gray-600 dark:text-gray-300 text-sm">Create your own rules and activities</p>
            </div>
            <div className="text-center">
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Decision Making</h4>
              <p className="text-gray-600 dark:text-gray-300 text-sm">Choose between restaurants, activities, or group preferences</p>
            </div>
            <div className="text-center">
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Random Selection</h4>
              <p className="text-gray-600 dark:text-gray-300 text-sm">Pick winners, assign tasks, or make unbiased choices</p>
            </div>
          </div>
        </div>
      </div>

      {/* Key Benefits */}
      <div className="space-y-8">
        <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg py-2 px-4">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 dark:text-white flex items-center justify-center whitespace-nowrap">
            <img src="/logo.png" alt="Spinner Games" className="w-5 h-5 sm:w-6 h-6 mr-2 sm:mr-3 flex-shrink-0 object-contain" />
            <span className="truncate">Key Benefits</span>
          </h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {benefits.map((benefit, index) => {
            const colors = getColorClasses(benefit.color);
            return (
              <div key={index} className={`${colors.bg} rounded-lg p-6 border ${colors.border}`}>
                <div className="flex items-center space-x-4 mb-4">
                  <div className={`${colors.icon}`}>
                    {benefit.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                    {benefit.category}
                  </h3>
                </div>
                <ul className="space-y-3">
                  {benefit.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-500 dark:text-green-400 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700 dark:text-gray-300 text-sm">
                        <strong>{item.split(':')[0]}:</strong> {item.split(':').slice(1).join(':')}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>

      {/* Why Spinner Games Matters Today */}
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-lg py-2 px-4">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 dark:text-white flex items-center justify-center whitespace-nowrap">
            <img src="/logo.png" alt="Spinner Games" className="w-5 h-5 sm:w-6 h-6 mr-2 sm:mr-3 flex-shrink-0 object-contain" />
            <span className="truncate">Why Spinner Games Matters Today</span>
          </h2>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 border border-gray-200 dark:border-gray-700">
        <p className="text-lg text-gray-600 dark:text-gray-300 mb-8 text-justify">
          In our increasingly digital world, genuine human connection has become more valuable than ever. Spinner Games addresses the growing need for:
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <Globe className="w-6 h-6 text-blue-600 dark:text-blue-300" />
              <h3 className="font-semibold text-gray-900 dark:text-white">Real Social Interaction</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm ml-9">
              Counteracting the isolation created by excessive screen time
            </p>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <Users className="w-6 h-6 text-green-600 dark:text-green-300" />
              <h3 className="font-semibold text-gray-900 dark:text-white">Intergenerational Bonding</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm ml-9">
              Creating opportunities for different age groups to connect
            </p>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <Lightbulb className="w-6 h-6 text-purple-600 dark:text-purple-300" />
              <h3 className="font-semibold text-gray-900 dark:text-white">Skill Development</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm ml-9">
              Building essential life skills that are often overlooked in digital environments
            </p>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <Heart className="w-6 h-6 text-pink-600 dark:text-pink-300" />
              <h3 className="font-semibold text-gray-900 dark:text-white">Mental Health</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm ml-9">
              Promoting positive social interactions that contribute to overall well-being
            </p>
          </div>
        </div>
        </div>
      </div>

      {/* Video Tutorial */}
      <div id="video-tutorial" className="space-y-8">
        <div className="bg-red-50 dark:bg-red-900/20 rounded-lg py-2 px-4">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 dark:text-white flex items-center justify-center whitespace-nowrap">
            <img src="/logo.png" alt="Spinner Games" className="w-5 h-5 sm:w-6 h-6 mr-2 sm:mr-3 flex-shrink-0 object-contain" />
            <span className="truncate">How to Play - Video Tutorial</span>
          </h2>
        </div>
        {/* Video Header - Centered */}
        <div className="text-center space-y-4">
          <div className="flex items-center justify-center">
            <div className="w-8 h-8 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center text-red-600 dark:text-red-300 mr-3">
              <Play className="w-4 h-4" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
              Watch How to Play Spinner Games
            </h3>
          </div>
          <p className="text-gray-600 dark:text-gray-300 text-center">
            Learn how to use Spinner Games with this step-by-step video tutorial. Perfect for beginners and families!
          </p>
        </div>

        {/* Video Container - Centered */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 border border-gray-200 dark:border-gray-700">
          <div className="max-w-4xl mx-auto">
            {/* YouTube Video Embed */}
            <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
              <iframe
                className="absolute top-0 left-0 w-full h-full rounded-lg"
                src="https://www.youtube.com/embed/HZcMRx72oEM"
                title="How to Play Spinner Games Tutorial"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              ></iframe>
            </div>
            
            <div className="mt-4 text-center">
              <a
                href="https://youtu.be/HZcMRx72oEM"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
              >
                <Play className="w-4 h-4 mr-2" />
                Watch on YouTube
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Getting Started */}
      <div className="space-y-6">
        <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-lg py-2 px-4">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 dark:text-white flex items-center justify-center whitespace-nowrap">
            <img src="/logo.png" alt="Spinner Games" className="w-5 h-5 sm:w-6 h-6 mr-2 sm:mr-3 flex-shrink-0 object-contain" />
            <span className="truncate">Getting Started</span>
          </h2>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 border border-gray-200 dark:border-gray-700">
          <p className="text-lg text-gray-600 dark:text-gray-300 text-justify">
            Simply choose your preferred activity, gather your group, and let Spinner Games facilitate meaningful connections, learning, and fun. Whether you're planning a family game night, organizing a classroom activity, or looking for a creative way to make decisions, Spinner Games provides the tools you need to bring people together.
          </p>
        </div>
        
        <div className="flex flex-col sm:flex-row justify-center gap-4">
          <button 
            onClick={() => {
              console.log('Start Playing Now clicked');
              // Scroll the main content area to top
              const mainContent = document.querySelector('.main-content');
              if (mainContent) {
                mainContent.scrollTo({ top: 0, behavior: 'smooth' });
              } else {
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }
            }}
            className="inline-flex items-center justify-center px-8 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors w-full sm:w-[240px]"
          >
            <Play className="w-5 h-5 mr-2" />
            Start Playing Now
          </button>
          <a
            href="/help/how-to-play"
            className="inline-flex items-center justify-center px-8 py-3 bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white font-semibold rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors w-full sm:w-[240px]"
          >
            Learn More
            <ArrowRight className="w-5 h-5 ml-2" />
          </a>
        </div>
      </div>

      {/* Footer Message */}
      <div className="text-center pt-8 pb-8">
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6 border border-blue-200 dark:border-blue-800">
          <p className="text-xl sm:text-2xl text-blue-600 dark:text-blue-400 font-semibold leading-relaxed">
            Transform your gatherings into memorable experiences that educate, entertain, and strengthen relationships.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LandingPageContent;
